<template>
    <div class="relative">
        <div class="fixed w-screen pt-3 pb-3 pr-3 pl-4 z-5 my-margin">
            <div class="flex justify-content-between">
                <img alt="logo" src="@/assets/logo.png" height="50" class="mr-2 text-blue-500 logo-bg">
                <span v-if="authStore.userIsLogged()" style="font-size: 1.25rem; font-weight: 500; margin-top: 8px;"
                    class="text-blue-500">{{ currentView }}</span>
                <Button @click="openMenu" v-if="authStore.userIsLogged()"
                    style="background-color: transparent; padding-left: 1.5rem;">
                    <i :class="['pi', 'pi-bars']" style="font-size: 1.5rem;" class="text-blue-500" />
                </Button>
            </div>
        </div>
    </div>
    <Sidebar v-model:visible="visible" position="right" class="surface-overlay" style="padding-top: 40px;">
    <template #header>
        <span class="m-auto text-white">شركاء للمكتبات الشخصية</span>
    </template>
    <template #closeicon>
        <i class="pi pi-times" style="padding-top: 40px; background-color: #263247; color: white; padding: 10px; border-radius: 40%;"></i>
    </template>

        <ul class="list-none p-3 m-0" dir="rtl">
            <ul class="list-none p-0 m-0 overflow-hidden">
                <li class="border-round-md flex justify-content-between align-items-center w-full">
                    <img id="profile_image" alt="avatar" :src="authStore.getUserProfilePhoto()" />
                    <div class="flex justify-content-center align-items-center gap-1">
                        <button v-if="menuEntries.length" @click="navigate(menuEntries[9].to)"
                            class="p-link surface-hover border-circle">
                            <i class="pi pi-cog text-gray-700" style="font-size: 1.25rem"></i>
                        </button>
                        <button @click="logout" class="p-link surface-hover border-circle">
                            <i class="pi pi-power-off text-gray-700" style="font-size: 1.25rem"></i>
                        </button>
                        <button @click="toggleTheme" class="p-link surface-hover border-circle">
                            <i :class="themeIcon" style="font-size: 1.25rem" class="text-gray-700"></i>
                        </button>
                    </div>
                </li>
                <Divider class="mb-0" />
                <li v-for="(e, index) in filteredMenuEntries" :key="index" @click="navigate(e.to)">
                    <a v-ripple
                        class="flex align-items-center cursor-pointer p-3 border-round text-color hover:surface-hover transition-duration-150 transition-colors p-ripple">
                        <i :class="[e.ficon, shouldBounce(e) ? 'animation-duration-1000 animation-iteration-infinite fadeout' : '']"
                            style="font-size: 1.25rem"></i>
                        <span style="cursor: pointer;" class="font-medium mr-3 ml-auto">{{ $t(e.label) }}</span>
                        <span v-if="showCounter(e)"
                            class="inline-flex align-items-center justify-content-center .mr-auto surface-50 border-circle font-medium text-primary hover:text-white"
                            style="min-width: 1.5rem; height: 1.5rem">{{ counter(e) }}</span>
                    </a>
                    <Divider v-if="index === 0 || index === 4 || index === 8" class="m-0" />
                </li>
            </ul>
        </ul>
    </Sidebar>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import { useAuthStore } from '@/stores/auth';
import { useI18n } from 'vue-i18n';
import { useConfirm } from "primevue/useconfirm";
import { useRoute } from 'vue-router';
import router from '@/router/router';
import menuHelper from "@/helpers/MenuHelper";
import { usePrimeVue } from 'primevue/config';
import { useThemeStore } from '@/helpers/theme';


const authStore = useAuthStore();
const i18n = useI18n();
const confirm = useConfirm();
const route = useRoute();
const $primevue = usePrimeVue();

const visible = ref(false);
const menuEntries = ref([]);
const currentView = ref();

const themeStore = useThemeStore();
const isDarkTheme = computed(() => themeStore.isDarkTheme);

const themeIcon = computed(() => (isDarkTheme.value ? 'pi pi-sun' : 'pi pi-moon'));

watch(route, () => {
    if (route.name !== '' && route.name !== 'login') {
        currentView.value = i18n.t(getTitlesMap()[route.name]);
    }
});

onMounted(() => {
    menuHelper.makeSideMenu(authStore.amIAdmin()).forEach(e => menuEntries.value.push(e));
    applyTheme();
});

const getTitlesMap = () => {
    return {
        'home': 'HOME',
        'controlPanel': 'HOME',
        'profile': 'PROFILE_MENU',
        'gift_request': 'GIFT',
        'borrowing_request': 'BORROW',
        'exchange_request': 'EXCHANGE',
        'operations': 'OPERATIONS',
        'messages': 'MESSAGES_MENU',
        'my_books': 'MY_BOOKS',
        'notifications': 'NOTIFICATIONS',
        'covers': 'COVER_CORRECTION',
        'user_join_requests': 'USER_JOIN_REQUESTS',
        'upload_books': 'UPLOAD_BOOKS',
        'import_books': 'UPLOAD_BOOKS',
        'books': 'BOOKS',
        'rewards': 'REWARDS'
    };
};

const filteredMenuEntries = computed(() => {
    return menuEntries.value.filter((_, index) => index !== 9);
});

const navigate = (url) => {
    if (url !== null && url !== undefined) {
        visible.value = false;
        setTimeout(() => {
            router.push(url);
        }, 500);
    }
};


const openMenu = () => {
    visible.value = true;
};

const logout = () => {
    confirm.require({
        header: i18n.t('CONFIRMATION_HEADER'),
        message: i18n.t('LOGOUT_CONFIRM_MESSAGE'),
        acceptLabel: i18n.t('YES'),
        rejectLabel: i18n.t('NO'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
            const authStore = useAuthStore();
            authStore.logout();
        }
    });
};

const shouldBounce = (item) => {
    return item.bounceVar !== undefined && authStore[item.bounceVar] > 0;
};

const counter = (item) => {
    return authStore[item.bounceVar] > 0 ? authStore[item.bounceVar] : 0;
};

const showCounter = (item) => {
    return counter(item) > 0;
};

const toggleTheme = () => {
    themeStore.toggleTheme();
    applyTheme();
  };
  
  const applyTheme = () => {
    const currentTheme = isDarkTheme.value ? 'md-dark-indigo' : 'md-light-indigo';
    const newTheme = isDarkTheme.value ? 'md-light-indigo' : 'md-dark-indigo';
    $primevue.changeTheme(currentTheme, newTheme, 'theme-link', () => {
      isDarkTheme.value = !isDarkTheme.value;
    });
  };
</script>

<style scoped>
.p-link {
    width: 3rem;
    height: 3rem;
    display: flex;
    justify-content: center;
    align-items: center;
}

img#profile_image {
    width: 48px;
    height: 48px;
    border-radius: 50%;
}

.side_bar_top {
    display: flex;
    justify-content: space-around;
    align-items: center;
    margin-top: 2em;
    padding-bottom: 0.5em;
}

.menu-entry {
    margin-bottom: 10px;
}

.menu-entry-icon {
    display: inline-block;
    width: 35px;
    text-align: center;
}

.counter-container {
    display: inline-block;
    width: 10px;
}

.counter {
    color: red;
    vertical-align: super;
    margin-right: -3px;
}

.my-margin{
    background-color: var(--surface-menu);
    margin-top: 0.9rem;
    margin-left: -0.5rem;
}

.logo-bg {
    background-color: var(--surface-logo);
    border-radius: 50%;
}


</style>
